import React, { useContext, useEffect, useMemo, useState } from "react";

import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";

import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import BackToDashboard from "../../components/BackToDashboard";
import EmployeeStatsTable from "./EmployeesStatsTable";
import axios from "axios";
import PeriodSearchFilter from "../../components/PeriodSearchFilter";
import InformationBox from "../../components/InformationBox";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";

const EmployeesProgress = () => {
  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();

  // STATES
  const [selectedEmployeeRows, setSelectedEmployeeRows] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState({
    title: t("employeeName"),
    value: "employeeName",
    type: "text",
  });
  const [selectedMonth, setSelectedMonth] = useState({
    value: new Date().getMonth(),
    name: "",
  });
  const [selectedYear, setSelectedYear] = useState({
    value: new Date().getFullYear(),
    name: new Date().getFullYear().toString(),
  });

  const [error, setError] = useState("");

  // STATIC
  const searchTitles = useMemo(
    () => [
      { title: t("employeeName"), value: "employeeName", type: "text" },
      { title: t("workRate"), value: "workRate", type: "text" },
    ],
    [t]
  );

  // ENDPOINTS

  const fetchAttendance = async () => {
    const token = localStorage.getItem("token");
    try {
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: 3,
        organizationId: parseInt(userData.organizationId),
      };

      if (searchQuery && selectedFilter?.value) {
        params.search = searchQuery;
        params.searchField = selectedFilter.value;
      }

      if (selectedMonth) {
        params.month = parseInt(selectedMonth.value);
      }

      if (selectedYear) {
        params.year = parseInt(selectedYear.value);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_URL}/api/attendance/getEmployeesAttendancies`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data.attendancies;

      if (res.status === 200) {
        setAttendanceData(data);
        setPagination((prev) => ({
          ...prev,
          totalPages: res.data.totalPages,
          totalRecords: res.data.totalAttendance,
        }));
      }
    } catch (error) {
      setError(error.response.data.message);
      console.error("Error fetching attendance", error);
    } finally {
      setIsLoading(false);
    }
  };

  // HANDLERS
  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    fetchAttendance();
  };

  // EFFECTS
  useEffect(() => {
    fetchAttendance();
  }, [pagination.currentPage, searchQuery]);

  return (
    <PageWrapper>
      <div className="u-padding--bottom_600">
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={
              userData?.userType === "user"
                ? "/invoiceManagement"
                : "/attendance-dashboard"
            }
          />
        </div>

        <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_600">
          <span>
            {userData?.userType === "organization"
              ? `${t("organization")}:`
              : `${t("user")}:`}
          </span>
          <span>{`${localStorage.getItem("username")}`}</span>
        </div>

        <h1>{t("stats")}</h1>

        <section className="u-margin--bottom_400">
          <PeriodSearchFilter
            options={searchTitles}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selected={selectedFilter}
            setSelected={setSelectedFilter}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            handleSearch={handleSearch}
          />
        </section>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <ErrorBox>{t(error)}</ErrorBox>
        ) : attendanceData.length == 0 ? (
          <InformationBox>{t("noAttendance")}</InformationBox>
        ) : (
          <EmployeeStatsTable
            data={attendanceData}
            pagination={pagination}
            setPagination={setPagination}
            selectedRows={selectedEmployeeRows}
            setSelectedRows={setSelectedEmployeeRows}
          />
        )}
      </div>
    </PageWrapper>
  );
};

export default EmployeesProgress;
